export type AppsFlyerParams = Record<string, string>;

const STORAGE_KEY = 'appsFlyerParams';
const AF_PREFIX = 'af_';
const EXTRA_KEYS = new Set(['pid', 'clickid', 'c']);

const isBrowser = typeof window !== 'undefined' && typeof window.sessionStorage !== 'undefined';

function normalizeSearch(search?: string | null): string {
  if (!search) {
    return '';
  }
  return search.startsWith('?') ? search.slice(1) : search;
}

function isAppsFlyerParam(key: string): boolean {
  return key.startsWith(AF_PREFIX) || EXTRA_KEYS.has(key);
}

export function extractAppsFlyerParams(search: string | URLSearchParams): AppsFlyerParams {
  const searchParams = typeof search === 'string' ? new URLSearchParams(normalizeSearch(search)) : search;
  const result: AppsFlyerParams = {};

  for (const [key, value] of searchParams.entries()) {
    if (isAppsFlyerParam(key) && value.trim()) {
      result[key] = value;
    }
  }

  return result;
}

export function persistAppsFlyerParams(search: string): AppsFlyerParams | null {
  if (!isBrowser) {
    return null;
  }

  const params = extractAppsFlyerParams(search);
  if (!Object.keys(params).length) {
    return null;
  }

  try {
    const mergedParams = {
      ...(getStoredAppsFlyerParams() ?? {}),
      ...params,
    };
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(mergedParams));
    return mergedParams;
  }
  catch (error) {
    console.warn('Не удалось сохранить AppsFlyer параметры в sessionStorage', error);
  }

  return params;
}

export function getStoredAppsFlyerParams(): AppsFlyerParams | null {
  if (!isBrowser) {
    return null;
  }

  const raw = window.sessionStorage.getItem(STORAGE_KEY);
  if (!raw) {
    return null;
  }

  try {
    return JSON.parse(raw) as AppsFlyerParams;
  }
  catch (error) {
    console.warn('Не удалось прочитать AppsFlyer параметры из sessionStorage', error);
    return null;
  }
}

export function clearAppsFlyerParams(): void {
  if (!isBrowser) {
    return;
  }

  window.sessionStorage.removeItem(STORAGE_KEY);
}

function removeAppsFlyerParamsFromCurrentUrl(): void {
  if (!isBrowser) {
    return;
  }

  const url = new URL(window.location.href);
  let changed = false;

  for (const key of Array.from(url.searchParams.keys())) {
    if (isAppsFlyerParam(key)) {
      url.searchParams.delete(key);
      changed = true;
    }
  }

  if (!changed) {
    return;
  }

  const relativeUrl = `${url.pathname}${url.search}${url.hash}`;
  window.history.replaceState(window.history.state, '', relativeUrl);
}

/**
 * Возвращает текущую атрибуцию для успешной оплаты и удаляет ее из URL/storage,
 * чтобы она не использовалась повторно в последующих редиректах.
 */
export function consumeAppsFlyerParams(search?: string): AppsFlyerParams | null {
  if (!isBrowser) {
    return null;
  }

  const currentParams = extractAppsFlyerParams(search ?? window.location.search);
  const params = {
    ...(getStoredAppsFlyerParams() ?? {}),
    ...currentParams,
  };

  clearAppsFlyerParams();
  removeAppsFlyerParamsFromCurrentUrl();

  return Object.keys(params).length ? params : null;
}
